import _ from "lodash";
import { Button } from "@commonsku/styles";
import React, { FC, useMemo, useState } from "react";
import styled from "styled-components";
import MetaDataList from "./MetaDataList";
import useSortable from "../../admin-new/hooks/useSortable";
import { DraggableItemContainer } from "../../DraggableItem";
import { Metadata } from "../../../interfaces/Metadata";
import { ItemRetailAdjustment } from "../../../interfaces/ItemRetailAdjustment";
import { getItemAdjustments } from "../../../selectors/items";
import { createAddItemRetailAdjustment, createUpdate } from "../../../actions";
import * as actions from "../../../actions/item";
import { useSelector, useDispatch } from "react-redux";

const updateItemRetailAdjustment = createUpdate('item-retail-adjustment', {
  request: actions.UPDATE_ITEM_RETAIL_ADJUSTMENT_REQUEST,
  success: actions.UPDATE_ITEM_RETAIL_ADJUSTMENT_SUCCESS,
  failure: actions.UPDATE_ITEM_RETAIL_ADJUSTMENT_FAILURE,
  failure_message: 'Unable to update retail adjustment'
});

const TD = styled.td`
  cursor: default !important;
`;

interface MetaDataProps {
  item_id?: string;
  parent_type: string;
  metaDataList: Metadata[];
  addMetaDataList: (userInput: string) => void;
  updateMetaDataList: (
    metaDataId: string,
    key: string,
    value?: string | number | object
  ) => (value: string | number | object) => void;
  updateMetadataListBatch: (metadataList: Metadata[]) => void;
  deleteMetaDataList: (metaDataId: string) => void;
  editMetaDataList: (metaDataId: string) => void;
}

const MetaData: FC<MetaDataProps> = ({
  item_id,
  parent_type,
  metaDataList,
  addMetaDataList,
  updateMetaDataList,
  updateMetadataListBatch,
  deleteMetaDataList,
  editMetaDataList,
}) => {
  const [newQuestion, setNewQuestion] = useState<string>("");
  const adjustments: ItemRetailAdjustment[] = useSelector((state) =>
    getItemAdjustments(state, { item_id: item_id }).filter(a => a.parent_type === "METADATA")
  );

  const dispatch = useDispatch();
  const onAddRetailAdjustment = (item_id, selector, value) => dispatch(createAddItemRetailAdjustment(item_id, selector, value));
  const onUpdateRetailAdjustment = (id, selector, prev, value) => dispatch(updateItemRetailAdjustment(id, selector, prev, value));

  const handleDeleteMetadata = (metadataId: string) => {
    const batch = [];
    let found = false;
    let order = 0;

    for (const i of items) {
      if (i.metadata_id === metadataId) {
        found = true;
        continue;
      }

      if (found) {
        batch.push({ ...i, display_order: order });
      }

      order++;
    }

    deleteMetaDataList(metadataId);
    if (batch.length > 0) {
      updateMetadataListBatch(batch);
    }
  };

  const [items, onMove, onDrop] = useSortable(metaDataList, updateMetadataListBatch, "display_order", true);

  const tableBody = useMemo(() => {
    return Object.values(items || {}).map((m: Metadata, index) => {
      const adjustment = m.type === 'TEXT' ? adjustments.find(a => a.parent_id === m.metadata_id && a.option == null) : undefined;
      const hasOptionAdjustment = m.type === 'SELECT' && adjustments.some(a => a.parent_id === m.metadata_id && a.option != null && a.adjustment != null);
      return (
        <DraggableItemContainer
          id={m.metadata_id}
          key={m.metadata_id}
          index={index}
          onDrop={onDrop}
          onMove={onMove}
          item={m}
          canDrag={Boolean(m)}
          endDrag={false}
          type="Item"
        >
          <MetaDataList
            key={m.metadata_id}
            metaDataId={m.metadata_id}
            label={m.label}
            type={m.type}
            parent_type={m.parent_type}
            properties={m.properties}
            item_id={item_id}
            item_retail_adjustment_id={adjustment?.item_retail_adjustment_id}
            hasOptionAdjustment={hasOptionAdjustment}
            adjustment={adjustment?.adjustment}
            mandatory={m.mandatory}
            updateMetadataList={updateMetaDataList}
            deleteMetaDataList={handleDeleteMetadata}
            editMetaDataList={editMetaDataList}
            onAddRetailAdjustment={onAddRetailAdjustment}
            onUpdateRetailAdjustment={onUpdateRetailAdjustment}
          />
        </DraggableItemContainer>
      );
    });
  }, [items, adjustments]);

  const handleAddQuestion = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (newQuestion.trim() === "") return;
    addMetaDataList(newQuestion.trim());
    setNewQuestion("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddQuestion(e);
    }
  };

  return (
    <div className="row">
      <div className="small-12 columns">
        {parent_type === "SHOP" && (
          <h5>
            Checkout Questions
            <span className="explanation" style={{ marginLeft: 8 }}>
              Allows you to request extra information from the customer
            </span>
          </h5>
        )}
        {parent_type === "ITEM" && <h2>Custom Product Questions</h2>}
        <div className="small-12 columns" style={{ padding: 0 }}>
          <div className="small-8 columns" style={{ padding: 0 }}>
            <input
              type="text"
              placeholder="Question"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="small-2 columns end">
            <Button
              size="small"
              variant={newQuestion.trim() ? undefined : "disabled"}
              style={{
                pointerEvents: newQuestion.trim() ? "visible" : "none",
              }}
              onClick={(e) => handleAddQuestion(e)}
            >
              Add
            </Button>
          </div>
        </div>
        <table>
          <thead style={{ fontSize: "0.875rem" }}>
            <tr>
              <TD></TD>
              <TD>Question</TD>
              <TD>Type</TD>
              {parent_type === "ITEM" && <TD>Character Limit</TD>}
              {parent_type === "ITEM" && <TD>Upcharge ($)</TD>}
              <TD>Mandatory</TD>
              <TD>Actions</TD>
            </tr>
          </thead>
          <tbody>
            {metaDataList && Object.values(metaDataList).length > 0 ? (
              tableBody
            ) : (
              <tr>
                <td>There are no questions to show here yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MetaData;
